import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import posthog from 'posthog-js'
import { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams, useSearchParams } from 'react-router'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { DotEntryForm, trackDotEntered, trackMetaEvent } from '@/lib/analytics'
import { CacheKeys } from '@/lib/cache'
import {
  confirmFunnelSession,
  type FunnelCarrier,
  recordExpertRequest,
  startFunnelSession,
  toFunnelCarrier,
  useFunnelSession,
} from '@/models/funnelSession'
import { InsuranceIndicationStatus } from '@/models/insuranceTypes'
import { dedupeCarriers, estimateToCard } from '@/routes/Public/carrierCard'
import { TalkToExpertDialog } from '@/routes/Public/TalkToExpertDialog'
import { BusinessConfirmPanel } from './BusinessConfirmPanel'
import { DotEntryPanel } from './DotEntryPanel'
import { withRangeMeta } from './getEstimatesData'
import { NewAuthorityPanel } from './NewAuthorityPanel'
import { ResultsPanel, type ResultsPhase } from './ResultsPanel'
import { ResultsSidebar } from './ResultsSidebar'
import { VerifyEmailDialog } from './VerifyEmailDialog'

const DOT_REGEX = /^\d{1,8}$/

// Kept in sync with the `lg:` breakpoint that splits the confirm step inline (desktop) vs. modal (mobile).
const DESKTOP_BREAKPOINT = 1024

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(true)
  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`)
    const onChange = () => setIsDesktop(mql.matches)
    onChange()
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])
  return isDesktop
}

type Phase = 'entry' | 'confirm' | 'loading' | 'results'
type Mode = 'dot' | 'new-authority'

/** Surface the backend's dot_number validation error. */
function dotErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined
  if (axios.isAxiosError(error) && error.response?.status === 400) {
    const data = error.response.data as Record<string, unknown> | undefined
    const dotErr = data?.dot_number
    if (typeof dotErr === 'string') return dotErr
    if (Array.isArray(dotErr) && typeof dotErr[0] === 'string') return dotErr[0]
  }
  return 'Something went wrong. Please try again.'
}

/**
 * Surface the backend's confirm error. A 400 carrying a detail is terminal —
 * today only a DOT another Cai account already holds (INS-319) — so confirming
 * again would just fail the same way; anything else is worth another try. That
 * one case is flagged, because the panel offers a code into the existing
 * account instead of only a way out.
 */
function confirmError(
  error: unknown
): { message: string; canRetry: boolean; claimedDot?: boolean } | undefined {
  if (!error) return undefined
  if (axios.isAxiosError(error) && error.response?.status === 400) {
    const data = error.response.data as Record<string, unknown> | undefined
    const detail = data?.detail
    if (typeof detail === 'string') {
      return { message: detail, canRetry: false, claimedDot: data?.claimed_dot === true }
    }
  }
  return { message: 'Something went wrong. Please try again.', canRetry: true }
}

/**
 * One-page Get Estimates flow, mounted under `ProductFlowLayout` at both
 * `/insurance/start` and `/insurance/estimates/:id`. Wired to the live
 * backend (start → poll → confirm → apply/expert). Desktop: interaction on
 * the left, results on the right (example → loading → real). Mobile: DOT
 * entry on top, example results below, confirm as a modal.
 */
export function GetEstimatesFlow() {
  const isDesktop = useIsDesktop()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { id: sessionId } = useParams<{ id?: string }>()
  const [searchParams] = useSearchParams()

  const [mode, setMode] = useState<Mode>('dot')
  const [fatalError, setFatalError] = useState(false)
  const [analysisDone, setAnalysisDone] = useState(false)
  const [expertOpen, setExpertOpen] = useState(false)
  const [verifyOpen, setVerifyOpen] = useState(false)
  // Brief transitional flag so the example stack's exit-upward animation plays before the loading swap.
  const [isExampleExiting, setIsExampleExiting] = useState(false)

  const startMutation = useMutation({
    mutationFn: (dot: string) => startFunnelSession(dot),
    onSuccess: (started) => {
      // Seed the poll cache so the confirm step has the carrier immediately.
      queryClient.setQueryData([CacheKeys.FUNNEL_SESSION, started.id], started)
      navigate(`/insurance/estimates/${started.id}`, { replace: true })
      posthog.capture('insure_dot_number_result', {
        dot_number: started.dotNumber,
        outcome: 'success',
      })
      // Fires here, not on submit: an unresolvable DOT 400s into onError with no lead written.
      trackDotEntered(started.dotNumber, DotEntryForm.ESTIMATES)
      trackMetaEvent(
        'Lead',
        { content_category: 'insurance_estimate', content_name: 'dot_submitted' },
        started.id
      )
    },
    onError: (error, dot) => {
      posthog.capture('insure_dot_number_result', {
        dot_number: dot,
        outcome: 'error',
        status: axios.isAxiosError(error) ? error.response?.status : undefined,
      })
    },
  })

  const { data: session } = useFunnelSession({
    id: sessionId,
    onFatalError: () => setFatalError(true),
  })

  const confirmMutation = useMutation({
    mutationFn: () => confirmFunnelSession(sessionId!),
    onSuccess: (data) => queryClient.setQueryData([CacheKeys.FUNNEL_SESSION, sessionId], data),
  })

  function handleApply() {
    posthog.capture('insure_complete_application_clicked', {
      funnel_session_id: sessionId,
      dot_number: session?.dotNumber ?? null,
    })
    setVerifyOpen(true)
  }

  const carrier = session ? toFunnelCarrier(session) : null
  const currentInsurance = session?.currentInsurer
    ? { insurer: session.currentInsurer, expirationDate: session.policyExpirationDate }
    : undefined

  // Estimates only — a lead-scoped funnel never produces live indications.
  const allCards = session ? dedupeCarriers(session.estimates.map(estimateToCard)) : []
  const pending = allCards.filter((c) => c.status === InsuranceIndicationStatus.PENDING)
  const generatedRaw = allCards
    .filter((c) => c.status === InsuranceIndicationStatus.GENERATED)
    .sort((a, b) => (a.perTruckLow ?? Infinity) - (b.perTruckLow ?? Infinity))
  const { cards: generated, rangeBounds } = withRangeMeta(generatedRaw)

  const hasCards = (session?.estimates.length ?? 0) > 0
  const hasResults = generated.length > 0
  const justConfirmed = confirmMutation.isPending || confirmMutation.isSuccess

  // Derive the display phase from real session state, keeping the example-exit animation window.
  let phase: Phase
  if (!sessionId) phase = 'entry'
  else if (isExampleExiting) phase = 'confirm'
  // A direct load of /insurance/estimates/:id has an id before the poll resolves — stay in loading, not confirm with a null carrier.
  else if (!session) phase = 'loading'
  else if (!hasCards && !justConfirmed) phase = 'confirm'
  // Stay in loading until the first card arrives, so a pending confirm can't fall through to the no-results state.
  else if (!analysisDone || !hasCards || (!hasResults && pending.length > 0)) phase = 'loading'
  else phase = 'results'

  const resultsPhase: ResultsPhase =
    phase === 'loading' ? 'loading' : phase === 'results' ? 'results' : 'example'

  // Record the generated estimates once per session, for insight into what each visitor is shown.
  const capturedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!sessionId || capturedRef.current === sessionId) return
    if ((session?.estimates.length ?? 0) === 0) return
    capturedRef.current = sessionId
    posthog.capture('insure_estimates_generated', {
      funnel_session_id: sessionId,
      dot_number: session?.dotNumber ?? null,
      estimate_count: session?.estimates.length ?? 0,
      estimates: (session?.estimates ?? []).map((e) => ({
        vendor: e.vendorName,
        carrier_name: e.carrierId ? e.vendorName : null,
        status: e.status,
        per_truck_low: e.perTruckLow,
        per_truck_high: e.perTruckHigh,
        truck_count: e.truckCount,
        decline_reason: e.declineReason || null,
      })),
    })
  }, [sessionId, session?.dotNumber, session?.estimates])

  function handleDotSubmit(dot: string, entry: 'manual' | 'deep_link') {
    posthog.capture('insure_dot_number_submitted', { dot_number: dot, entry })
    startMutation.mutate(dot)
  }

  // Deep-link entry: `?dot=1234567` auto-submits, but only without an existing sessionId, so it can't hijack an /estimates/:id link.
  const deepLinkDot = searchParams.get('dot')?.trim() ?? ''
  const autoSubmittedRef = useRef(false)
  useEffect(() => {
    if (autoSubmittedRef.current || sessionId || !DOT_REGEX.test(deepLinkDot)) return
    autoSubmittedRef.current = true
    handleDotSubmit(deepLinkDot, 'deep_link')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkDot, sessionId])

  function handleConfirm() {
    // Kick off the real confirm now; the example stack plays its exit-upward animation into the loading swap.
    setIsExampleExiting(true)
    confirmMutation.mutate()
    posthog.capture('insure_confirm_carrier', { funnel_session_id: sessionId })
    setTimeout(() => setIsExampleExiting(false), 320)
    // Mobile: send the visitor to the top so they see the sidebar content first.
    if (!isDesktop) {
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
    }
  }

  function handleReset() {
    setFatalError(false)
    setAnalysisDone(false)
    setIsExampleExiting(false)
    confirmMutation.reset()
    startMutation.reset()
    navigate('/insurance/start', { replace: true })
  }

  const showInlineConfirm = phase === 'confirm' && isDesktop

  return (
    <>
      <Helmet>
        <title>{phase === 'entry' ? 'Get an insurance estimate' : 'Your insurance estimate'}</title>
      </Helmet>

      <div className="mx-auto w-full max-w-6xl px-5.5 py-10">
        {fatalError ? (
          <FatalError onReset={handleReset} />
        ) : mode === 'new-authority' ? (
          <NewAuthorityPanel onBack={() => setMode('dot')} />
        ) : (
          <>
            <div className="grid gap-8 lg:grid-cols-3 lg:gap-12">
              {/* LEFT (desktop) / TOP (mobile): the interaction column — 1 of 3. */}
              <div className="flex flex-col">
                {phase === 'entry' || (phase === 'confirm' && !isDesktop) ? (
                  <DotEntryPanel
                    isLookingUp={startMutation.isPending || phase === 'confirm'}
                    errorMessage={dotErrorMessage(startMutation.error)}
                    onSubmit={(dot) => handleDotSubmit(dot, 'manual')}
                    onNewAuthority={() => setMode('new-authority')}
                  />
                ) : showInlineConfirm ? (
                  <BusinessConfirmPanel
                    carrier={carrier}
                    sessionId={sessionId}
                    isConfirming={isExampleExiting}
                    error={confirmError(confirmMutation.error)}
                    onConfirm={handleConfirm}
                    onReject={handleReset}
                  />
                ) : phase === 'results' ? (
                  <ResultsSidebar
                    carrier={carrier}
                    currentInsurance={currentInsurance}
                    onApply={handleApply}
                    onTalkToExpert={() => {
                      posthog.capture('insure_contact_expert_clicked', {
                        funnel_session_id: sessionId,
                        dot_number: session?.dotNumber ?? null,
                      })
                      setExpertOpen(true)
                    }}
                  />
                ) : (
                  <ConfirmedBusinessSummary carrier={carrier} />
                )}
              </div>

              {/* RIGHT (desktop) / BELOW (mobile): the results column — spans 2 of 3. */}
              <div className="flex flex-col lg:col-span-2">
                <ResultsPanel
                  phase={resultsPhase}
                  onAnalysisDone={() => setAnalysisDone(true)}
                  cards={generated}
                  rangeBounds={rangeBounds}
                  pendingCount={pending.length}
                  exampleDimmed={phase === 'confirm' && !isExampleExiting}
                  exampleExiting={isExampleExiting}
                />
              </div>
            </div>

            {/* Mobile-only confirm modal. */}
            <Dialog open={phase === 'confirm' && !isDesktop} onOpenChange={(open) => !open && handleReset()}>
              <DialogContent>
                {/* Title kept for the dialog's accessible name, visually hidden. */}
                <DialogTitle className="sr-only">Confirm your business</DialogTitle>
                <BusinessConfirmPanel
                  carrier={carrier}
                  sessionId={sessionId}
                  isConfirming={isExampleExiting}
                  error={confirmError(confirmMutation.error)}
                  onConfirm={handleConfirm}
                  onReject={handleReset}
                />
              </DialogContent>
            </Dialog>

            {sessionId && (
              <VerifyEmailDialog open={verifyOpen} onOpenChange={setVerifyOpen} sessionId={sessionId} />
            )}

            {sessionId && (
              <TalkToExpertDialog
                open={expertOpen}
                onOpenChange={setExpertOpen}
                submit={(c) => recordExpertRequest(sessionId, c)}
              />
            )}
          </>
        )}
      </div>
    </>
  )
}

function ConfirmedBusinessSummary({ carrier }: { carrier: FunnelCarrier | null }) {
  const driverCount = carrier?.drivers ?? 0
  const summary = [
    carrier?.dotNumber && `DOT ${carrier.dotNumber}`,
    driverCount > 0 && `${driverCount} driver${driverCount === 1 ? '' : 's'}`,
    carrier?.state,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="flex flex-col gap-1">
      <span className="eyebrow self-start">Estimating for</span>
      <h3 className="text-xl font-bold">{carrier?.name || 'Your business'}</h3>
      {summary && <span className="text-muted-foreground text-sm">{summary}</span>}
    </div>
  )
}

function FatalError({ onReset }: { onReset: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
      <h1 className="text-2xl font-bold">We couldn&apos;t load this estimate</h1>
      <p className="text-muted-foreground max-w-md text-sm">
        The link may have expired. Start over with your DOT number.
      </p>
      <Button className="mt-2" onClick={onReset}>
        Start over
      </Button>
    </div>
  )
}
